import { useEffect, useRef, useState } from 'react';

export type StopKind = 'stop' | 'force';

interface StopRunDialogProps {
  jobId: string;
  open: boolean;
  /** Start on Force stop: the run reports a terminal status but still holds
   *  resources, so a graceful stop has nothing to do. */
  terminal?: boolean;
  busy: StopKind | null;
  error: string | null;
  onConfirm: (kind: StopKind) => void;
  onClose: () => void;
}

/**
 * One way to stop a run. Stop is the action; Force stop is the escalation,
 * one click further in, explained at the moment of choosing it rather than
 * offered as a peer everywhere.
 */
export function StopRunDialog({
  jobId,
  open,
  terminal = false,
  busy,
  error,
  onConfirm,
  onClose,
}: StopRunDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const initial: StopKind = terminal ? 'force' : 'stop';
  const [kind, setKind] = useState<StopKind>(initial);
  // Closing forgets an escalation, so the next open starts at Stop again.
  const close = () => {
    setKind(initial);
    onClose();
  };
  // Escape must not dismiss a request in flight: the result needs a home.
  const cancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
    if (busy !== null) event.preventDefault();
    else close();
  };

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  const force = kind === 'force';
  return (
    <dialog
      ref={ref}
      onClose={close}
      onCancel={cancel}
      aria-labelledby="stop-run-title"
      className="pane m-auto w-[27rem] max-w-[calc(100vw-2rem)] p-0 text-ink shadow-lg backdrop:bg-black/35"
    >
      <div className="px-4 pt-4 pb-3">
        <h2 id="stop-run-title" className="text-sm font-semibold">
          {force ? 'Force stop' : 'Stop'}{' '}
          <span className="font-mono text-xs font-normal text-ink-2">
            {jobId}
          </span>
          ?
        </h2>
        <p className="mt-1.5 text-xs leading-4 text-ink-2">
          {force
            ? 'Kills the runner and tears down its Kubernetes resources immediately. Logs and results written so far are kept.'
            : 'Active samples are scored with their partial work, results are written, and the run exits on its own.'}
        </p>
        {force && !terminal && (
          <p className="callout callout-warn mt-3">
            Use this when a normal stop did not take, or the run is stuck.
          </p>
        )}
        {error && <p className="callout callout-err mt-3">{error}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => onConfirm(kind)}
          className={force ? 'btn btn-destructive' : 'btn btn-primary'}
        >
          {busy !== null ? 'Stopping…' : force ? 'Force stop' : 'Stop'}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          onClick={close}
          className="btn btn-ghost"
        >
          Cancel
        </button>
        {!terminal &&
          (force ? (
            <button
              type="button"
              onClick={() => setKind('stop')}
              className="row-action ml-auto"
            >
              Back to Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setKind('force')}
              className="row-action row-action-danger ml-auto"
            >
              Force stop instead…
            </button>
          ))}
      </div>
    </dialog>
  );
}
